<template>
  <div class="relative">
    <MinimalButton
      v-if="isOpen"
      @click="clearFilters"
      dusk="clear"
      class="absolute right-0"
      title="Clear Filters"
    >
      <FontAwesomeIcon
        :icon="faFilterCircleXmark"
        class="h-4 text-red-500 hover:text-red-700"
      />
    </MinimalButton>

    <div
      class="border border-gray-200 pt-10 pb-4 px-4 rounded shadow-md"
      v-if="isOpen"
    >
      <input
        @keypress.enter="submit"
        v-model="searchRef"
        type="text"
        dusk="search"
        class="border border-gray-200 rounded w-full mb-4"
        placeholder="Search"
        aria-label="Search Here"
      />

      <div v-if="availableTags.length" class="mb-4">
        <button
          type="button"
          @click="tagsExpanded = !tagsExpanded"
          class="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900 mb-2 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-1 rounded"
          :aria-expanded="tagsExpanded"
          aria-controls="tag-filter-panel"
          dusk="toggle-tag-filter"
        >
          <FontAwesomeIcon
            :icon="tagsExpanded ? faChevronDown : faChevronRight"
            class="h-3"
            aria-hidden="true"
          />
          <span>
            Filter by Tag<span v-if="selectedTags.length"> ({{ selectedTags.length }})</span>
          </span>
        </button>

        <div
          v-if="!tagsExpanded && selectedTags.length"
          class="flex flex-wrap gap-1.5"
          aria-label="Active tag filters"
        >
          <span
            v-for="tag in selectedTags"
            :key="`active-${tag}`"
            class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-600 text-white"
          >
            {{ tag }}
            <button
              type="button"
              @click="toggleTag(tag)"
              :aria-label="`Remove tag filter ${tag}`"
              class="cursor-pointer hover:text-indigo-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white rounded-full"
              :dusk="`active-filter-tag-${tag}`"
            >
              ×
            </button>
          </span>
        </div>

        <div
          v-show="tagsExpanded"
          id="tag-filter-panel"
          class="flex flex-wrap gap-1.5"
          role="group"
          aria-label="Available tags"
        >
          <button
            v-for="tag in availableTags"
            :key="tag.id ?? tag.name ?? tag"
            type="button"
            @click="toggleTag(tagName(tag))"
            :aria-pressed="isTagSelected(tagName(tag))"
            :class="[
              'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium transition cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-1',
              isTagSelected(tagName(tag))
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            ]"
            :dusk="`filter-tag-${tagName(tag)}`"
          >
            {{ tagName(tag) }}
          </button>
        </div>
      </div>

      <MinimalButton
        @click="submit"
        dusk="submit"
      >
        Apply Filters
      </MinimalButton>

    </div>

  </div>

</template>

<script setup>
import { ref, defineProps, onMounted } from 'vue';
import MinimalButton from './MinimalButton.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faFilterList, faFilterCircleXmark, faChevronDown, faChevronRight } from '@fortawesome/pro-regular-svg-icons';


const props = defineProps({
    inputs: {
      type: Object,
      required: true,
    },
    availableTags: {
      type: Array,
      default: () => [],
    },
});

const isOpen = ref(true);

const searchRef = ref(props.inputs.search);

const initialSelectedFromInputs = Array.isArray(props.inputs.tags)
  ? props.inputs.tags
  : (props.inputs.tags ? Object.values(props.inputs.tags) : []);

const tagsExpanded = ref(false);

const tagName = (tag) => {
  if (typeof tag === 'string') return tag;
  return tag?.name ?? '';
};

const selectedTags = ref(initialSelectedFromInputs.map(tagName).filter(Boolean));

function isTagSelected(name) {
  return selectedTags.value.includes(name);
}

function toggleTag(name) {
  if (!name) return;
  if (isTagSelected(name)) {
    selectedTags.value = selectedTags.value.filter((t) => t !== name);
  } else {
    selectedTags.value = [...selectedTags.value, name];
  }
}

onMounted(() => {
  if (props.inputs.search || selectedTags.value.length) {
    isOpen.value = true;
  }
});

function clearFilters() {
  searchRef.value = '';
  selectedTags.value = [];
  submit();
}

function submit() {
  const searchParams = new URLSearchParams();
  if (searchRef.value) {
    searchParams.set('search', searchRef.value);
  }
  if (typeof props.inputs.archived !== 'undefined') {
    searchParams.set('archived', true);
  }
  for (const tag of selectedTags.value) {
    searchParams.append('tags[]', tag);
  }
  window.location.search = searchParams.toString();
}

</script>
